///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Pulls the seismic sequence around a mainshock out of the local SQLite
/// catalog. Membership is a DBSCAN cluster (ST_ClusterDBSCAN) over the
/// candidates in a local km frame, so the mainshock's foreshocks and
/// aftershocks come out as one group and unrelated background seismicity in
/// the same bounding box stays separate. The block's orientation and size
/// then come from the principal axes of the members (cutoutLayout).
///

import { Database } from "@/earthbrowser/database/Database"
import type { EarthquakeProperties } from "@/earthbrowser/dataset-distribution/earthquakes/EarthquakesTable"
import { clusterEpsKm, searchRadiusKm, type SequenceEvent } from "./cutoutLayout"

const cKmPerDegree = 111.32
const cMinClusterPoints = 3
/** Hard cap on events built into the cloud; largest magnitudes win */
const cMaxEvents = 3000

export interface QuakeSequence {
	mainshock: EarthquakeProperties
	events: SequenceEvent[]
	/** Cluster members (including the mainshock) */
	sequenceCount: number
	foreshockCount: number
	/** Largest cluster member after the mainshock */
	largestAftershockMag?: number
	epsKm: number
}

type CandidateRow = {
	id: string
	lat: number
	lon: number
	depth_km: number
	mag: number
	event_time: string
	x: number
	y: number
	cluster: number | null
}

/**
 * Candidates in the search box around the epicenter, each tagged with its
 * DBSCAN cluster id (null = noise). Coordinates are local equirectangular
 * km so eps is a distance, not degrees. ST_Collect aggregates in the order
 * the ordered subquery yields, which is what makes the JSON index line up
 * with row_number()
 */
const cCandidatesSql = `
	with c as (
		select
			e.id, e.lat, e.lon, e.depth_km, e.mag, e.event_time,
			(e.lon - ?) * ? as x,
			(e.lat - ?) * ? as y,
			row_number() over (order by e.event_time, e.id) - 1 as idx
		from earthquakes e
		join earthquakes_idx i on i.id = e.rowid
		where i.min_x >= ? and i.max_x <= ? and i.min_y >= ? and i.max_y <= ?
			and e.type = 'earthquake'
	),
	cl as (
		select json_extract(value, '$.index') as idx, json_extract(value, '$.cluster') as cluster
		from json_each((
			select ST_ClusterDBSCAN(ST_Collect(ST_MakePoint(x, y)), ?, ${cMinClusterPoints})
			from (select x, y from c order by idx)
		))
	)
	select c.id, c.lat, c.lon, c.depth_km, c.mag, c.event_time, c.x, c.y, cl.cluster
	from c left join cl on cl.idx = c.idx
	order by c.idx`

export class QuakeSequenceQuery {
	public static async load(mainshock: EarthquakeProperties): Promise<QuakeSequence> {
		const lat0 = mainshock.lat
		const lon0 = mainshock.lon
		const kmLat = cKmPerDegree
		const kmLon = cKmPerDegree * Math.cos(lat0 * Math.PI / 180)
		const radiusKm = searchRadiusKm(mainshock.mag)
		const epsKm = clusterEpsKm(mainshock.mag)
		const dLat = radiusKm / kmLat
		const dLon = radiusKm / Math.max(kmLon, 1)

		const rows = await Database.db.exec(cCandidatesSql, [
			lon0, kmLon, lat0, kmLat,
			lon0 - dLon, lon0 + dLon, lat0 - dLat, lat0 + dLat,
			epsKm
		], "high") as CandidateRow[]

		const mainRow = rows.find(r => r.id === mainshock.id)
		const mainCluster = mainRow?.cluster ?? null
		const mainTime = Date.parse(mainshock.event_time)

		let events: SequenceEvent[] = rows.map(r => ({
			id: r.id,
			lat: r.lat,
			lon: r.lon,
			depthKm: Math.max(0, r.depth_km ?? 0),
			mag: r.mag ?? 0,
			timeMs: Date.parse(r.event_time),
			inSequence: r.id === mainshock.id || (mainCluster !== null && r.cluster === mainCluster),
			east: r.x,
			north: r.y
		}))

		// The mainshock may be older than the scene window but must always be present
		if(!mainRow) {
			events.push({
				id: mainshock.id,
				lat: lat0,
				lon: lon0,
				depthKm: Math.max(0, mainshock.depth_km ?? 0),
				mag: mainshock.mag,
				timeMs: mainTime,
				inSequence: true,
				east: 0,
				north: 0
			})
		}

		if(events.length > cMaxEvents) {
			events.sort((a, b) => (b.inSequence ? 1 : 0) - (a.inSequence ? 1 : 0) || b.mag - a.mag)
			events = events.slice(0, cMaxEvents)
		}

		const members = events.filter(e => e.inSequence)

		let largestAftershockMag: number | undefined
		let foreshockCount = 0
		for(const e of members) {
			if(e.id === mainshock.id) continue
			if(e.timeMs < mainTime) foreshockCount++
			else largestAftershockMag = Math.max(largestAftershockMag ?? -Infinity, e.mag)
		}

		return {
			mainshock,
			events,
			sequenceCount: members.length,
			foreshockCount,
			largestAftershockMag,
			epsKm
		}
	}
}
